import { useDesignMode } from "@/hooks/useDesignMode";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LAYER_PRESETS, getLayerLabel } from "./layerConstants";
import {
  X,
  Trash2,
  Move,
  RotateCw,
  Layers,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Pin,
} from "lucide-react";

const PAGES = [
  { value: "all", label: "Toutes les pages" },
  { value: "home", label: "Accueil" },
  { value: "auth", label: "Connexion" },
  { value: "subscription", label: "Abonnement" },
  { value: "dashboard", label: "Dashboard Pro" },
  { value: "search", label: "Recherche" },
  { value: "messages", label: "Messages" },
];

const DesignModePanel = () => {
  const {
    isDesignMode,
    isPanelOpen,
    setPanelOpen,
    selectedElementId,
    selectElement,
    elements,
    updateElement,
    setElements,
    setHasUnsavedChanges,
  } = useDesignMode();

  if (!isDesignMode || !isPanelOpen || !selectedElementId) return null;

  const el = elements.find((e) => e.id === selectedElementId);
  if (!el) return null;

  const handleDelete = async () => {
    if (el.type === "overlay") {
      const overlay = el;
      if (overlay.image_url) {
        const urlParts = overlay.image_url.split("/theme-assets/");
        if (urlParts.length > 1) {
          await supabase.storage.from("theme-assets").remove([urlParts[1]]);
        }
      }
      await supabase.from("page_overlays" as any).delete().eq("id", el.id);
    }
    setElements((prev) => prev.filter((e) => e.id !== el.id));
    selectElement(null);
    toast({ title: "🗑️ Élément supprimé" });
  };

  // Removed old moveZIndex — now using layer presets

  return (
    <div
      data-design-panel
      data-editor-ui
      className="fixed right-4 top-20 z-[9998] w-72 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-muted/50 border-b border-border">
        <span className="text-sm font-semibold truncate">
          {el.label || el.type === "overlay" ? "Image" : "Texte"}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            onClick={handleDelete}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              selectElement(null);
              setPanelOpen(false);
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="p-3 space-y-4 max-h-[60vh] overflow-y-auto text-sm">
        {/* Label */}
        {el.type === "overlay" && (
          <div>
            <Label className="text-xs">Nom</Label>
            <Input
              value={el.label || ""}
              onChange={(e) => updateElement(el.id, { label: e.target.value })}
              className="h-8 text-xs"
            />
          </div>
        )}

        {/* Active toggle */}
        <div className="flex items-center justify-between">
          <Label className="text-xs flex items-center gap-1">
            {el.is_active !== false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            Visible
          </Label>
          <Switch
            checked={el.is_active !== false}
            onCheckedChange={(v) => updateElement(el.id, { is_active: v })}
          />
        </div>

        {/* Page target */}
        {el.type === "overlay" && (
          <div>
            <Label className="text-xs">Page cible</Label>
            <Select
              value={el.page || "all"}
              onValueChange={(v) => updateElement(el.id, { page: v })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Position */}
        <div>
          <Label className="text-xs flex items-center gap-1">
            <Move className="w-3 h-3" /> Position
          </Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <div>
              <span className="text-[10px] text-muted-foreground">X: {Math.round(el.position_x)}%</span>
              <Slider
                value={[el.position_x]}
                min={0}
                max={100}
                step={0.5}
                onValueChange={([v]) => updateElement(el.id, { position_x: v })}
              />
            </div>
            <div>
              <span className="text-[10px] text-muted-foreground">Y: {Math.round(el.position_y)}%</span>
              <Slider
                value={[el.position_y]}
                min={0}
                max={100}
                step={0.5}
                onValueChange={([v]) => updateElement(el.id, { position_y: v })}
              />
            </div>
          </div>
        </div>

        {/* Size */}
        <div>
          <Label className="text-xs">Taille</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <div>
              <span className="text-[10px] text-muted-foreground">L: {Math.round(el.width)}px</span>
              <Slider
                value={[el.width]}
                min={20}
                max={800}
                step={5}
                onValueChange={([v]) => updateElement(el.id, { width: v })}
              />
            </div>
            <div>
              <span className="text-[10px] text-muted-foreground">H: {Math.round(el.height)}px</span>
              <Slider
                value={[el.height]}
                min={20}
                max={800}
                step={5}
                onValueChange={([v]) => updateElement(el.id, { height: v })}
              />
            </div>
          </div>
        </div>

        {/* Rotation */}
        <div>
          <Label className="text-xs flex items-center gap-1">
            <RotateCw className="w-3 h-3" /> Rotation: {el.rotation}°
          </Label>
          <Slider
            value={[el.rotation]}
            min={-180}
            max={180}
            step={1}
            onValueChange={([v]) => updateElement(el.id, { rotation: v })}
          />
        </div>

        {/* Opacity */}
        <div>
          <Label className="text-xs">Opacité: {el.opacity}%</Label>
          <Slider
            value={[el.opacity]}
            min={5}
            max={100}
            step={5}
            onValueChange={([v]) => updateElement(el.id, { opacity: v })}
          />
        </div>

        {/* Layer preset */}
        <div>
          <Label className="text-xs flex items-center gap-1">
            <Layers className="w-3 h-3" /> Calque: {getLayerLabel(el.z_index)}
          </Label>
          <Select
            value={String(el.z_index)}
            onValueChange={(v) => updateElement(el.id, { z_index: parseInt(v) })}
          >
            <SelectTrigger className="h-8 text-xs mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LAYER_PRESETS.map((preset) => (
                <SelectItem key={preset.label} value={String(preset.z)}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Lock */}
        <div className="flex items-center justify-between">
          <Label className="text-xs flex items-center gap-1">
            {el.isLocked ? <Lock className="w-3 h-3 text-amber-500" /> : <Unlock className="w-3 h-3" />}
            Verrouiller
          </Label>
          <Switch
            checked={!!el.isLocked}
            onCheckedChange={(v) => updateElement(el.id, { isLocked: v })}
          />
        </div>

        {/* Position Type (fixed/scroll/sticky) */}
        {el.type === "overlay" && (
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Pin className="w-3 h-3" /> Type de position
            </Label>
            <Select
              value={el.positionType || "fixed"}
              onValueChange={(v) => updateElement(el.id, { positionType: v as "fixed" | "absolute" | "sticky" })}
            >
              <SelectTrigger className="h-8 text-xs mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixe (ne défile pas)</SelectItem>
                <SelectItem value="absolute">Scroll (défile avec la page)</SelectItem>
                <SelectItem value="sticky">Sticky (se fixe au scroll)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Preview */}
        {el.type === "overlay" && el.image_url && (
          <div className="relative bg-muted rounded-lg h-24 overflow-hidden border flex items-center justify-center">
            <img
              src={el.image_url}
              alt={el.label || ""}
              className="max-h-full max-w-full object-contain"
              style={{
                transform: `rotate(${el.rotation}deg)`,
                opacity: el.opacity / 100,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DesignModePanel;
